import * as React from 'react'
import styled from 'styled-components'
import ReactSelect from 'react-select'
import { tJa } from '../../../core/i18n'
import * as constants from '../../../static/constants'
import * as interfaces from '../../../static/interfaces'
import { IDmcTourParams, IDmcTourMeetingPointParams } from '../../../core/services/DmcTourService'
import {
  Button,
  CheckBox,
  InputLabel,
  InputRadio,
  InputText,
  InputTextArea,
  Select,
} from '../../atoms'
import Required from '../../atoms/required'
import DefaultTemplateGroups, { TemplateResource } from '../../molecules/DefaultTemplateGroups'
import TourSkillSingleSelect from '../../molecules/TourSkill/TourSkillSingleSelect'

declare var window: interfaces.IWindow

export type IDmcTourFormValues = IDmcTourParams & { title_same_as_short_name: boolean }

// ページ上部固定のアンカーリンク。id はセクションの id と対応させる
export const TOUR_SECTIONS = [
  { id: 'dmc-basic', label: 'post.basic_info' },
  { id: 'dmc-detail', label: 'post.detail_info' },
  { id: 'dmc-guide-fee', label: 'generic.guide_fee' },
  { id: 'dmc-address', label: 'generic.meeting_point' },
  { id: 'dmc-cost', label: 'activerecord.models.post_cost' },
  { id: 'dmc-settings', label: 'generic.setting' },
]

const POST_TYPE_OPTIONS = [
  { id: 'private', value: 'private', label: tJa('dmc_tour.private_tour') },
  { id: 'group', value: 'group', label: tJa('dmc_tour.group_tour') },
]

// グループツアーの内訳: 貸切(exclusive=true) / カスタム(exclusive=false)
const GROUP_KIND_OPTIONS = [
  { id: 'exclusive_group', value: 'true', label: tJa('dmc_tour.exclusive_group') },
  { id: 'custom_group', value: 'false', label: tJa('dmc_tour.custom_group') },
]

const SPECIAL_ACTIVITY_FEE_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9].map(
  num => `special_activity_fee_${num}`
)

const buildGuideMeetingTimeOffsetOptions = () => [
  { value: '', label: tJa('generic.guide_meeting_time_offset_unset') },
  ...Array.from({ length: 19 }, (_, i) => {
    const minutes = i * 5
    return {
      value: String(minutes),
      label: tJa('generic.guide_meeting_time_offset_minutes_before', { minutes }),
    }
  }),
]

// 選択済み言語のチップ配色(モック tour/Main.dc.html)
const LANGUAGE_SELECT_STYLES = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  multiValue: base => ({
    ...base,
    backgroundColor: '#e8f4f0',
    border: '1px solid #bfded4',
    borderRadius: 3,
  }),
  multiValueLabel: base => ({ ...base, color: '#0e6d55' }),
}

const emptyMeetingPoint: IDmcTourMeetingPointParams = {
  name: '',
  location: '',
  latitude: null,
  longitude: null,
  memo: '',
}

interface IProps {
  google: any
  isNew: boolean
  values: IDmcTourFormValues
  onChange: (values: IDmcTourFormValues) => void
  areas: interfaces.IArea[]
  languages: interfaces.ILanguage[]
  agentRanks: interfaces.IAgentRank[]
  tourSkills: interfaces.ITourSkill[]
  // 編集時のみ: テンプレは owner post に紐づいたものだけを扱う
  ownerPostId?: number
  specialRanks?: interfaces.IAgentRank[]
  soundOutTemplates?: any[]
  formalRequestTemplates?: any[]
}

export const TourForm: React.FC<IProps> = props => {
  const { values, onChange } = props
  const update = (patch: Partial<IDmcTourFormValues>) => onChange({ ...values, ...patch })

  // ツアー言語: 検索付き複数選択。English は入力中も常に先頭に表示する
  const languageOptions = React.useMemo(() => {
    const english = props.languages.filter(language => language.code === 'en')
    const others = props.languages.filter(language => language.code !== 'en')
    return [...english, ...others].map(language => ({
      value: language.id,
      label: language.name,
      isEnglish: language.code === 'en',
    }))
  }, [props.languages])
  const selectedLanguages = languageOptions.filter(option =>
    values.language_ids.includes(option.value)
  )
  const filterLanguageOption = (option, input: string) =>
    option.data.isEnglish || option.label.toLowerCase().startsWith(input.toLowerCase())

  // 集合場所: ローカルの配列を編集し、保存時にまとめて送る
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null)
  const [draft, setDraft] = React.useState<IDmcTourMeetingPointParams>(emptyMeetingPoint)
  const mapRef = React.useRef<any>(null)
  const markerRef = React.useRef<any>(null)

  React.useEffect(() => {
    if (editingIndex === null || !props.google) {
      return
    }
    const input = document.getElementById('dmc-meeting-point-location') as HTMLInputElement
    if (!input) {
      return
    }
    const autocomplete = new props.google.maps.places.Autocomplete(input)
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      if (!place.geometry) {
        return
      }
      setDraft(current => ({
        ...current,
        location: input.value,
        latitude: String(place.geometry.location.lat()),
        longitude: String(place.geometry.location.lng()),
      }))
    })
    return () => props.google.maps.event.clearInstanceListeners(autocomplete)
  }, [editingIndex, props.google])

  React.useEffect(() => {
    if (editingIndex === null || !props.google || !draft.latitude || !draft.longitude) {
      return
    }
    const latLng = new props.google.maps.LatLng(Number(draft.latitude), Number(draft.longitude))
    if (!mapRef.current) {
      mapRef.current = new props.google.maps.Map(document.getElementById('dmc-meeting-point-map'), {
        zoom: 15,
        center: latLng,
        mapTypeId: 'roadmap',
      })
      markerRef.current = new props.google.maps.Marker({ position: latLng, map: mapRef.current })
    } else {
      mapRef.current.setCenter(latLng)
      markerRef.current.setPosition(latLng)
    }
  }, [editingIndex, draft.latitude, draft.longitude, props.google])

  const startEditMeetingPoint = (index: number) => {
    setDraft(index >= 0 ? values.meeting_points[index] : emptyMeetingPoint)
    setEditingIndex(index)
  }
  const closeMeetingPointEditor = () => {
    setEditingIndex(null)
    mapRef.current = null
    markerRef.current = null
  }
  const saveMeetingPoint = () => {
    if (!draft.name.trim() || !draft.location.trim()) {
      window.flashMessages.addMessage({
        text: tJa('dmc_tour.meeting_point_required'),
        type: 'error',
      })
      return
    }
    const meeting_points =
      editingIndex >= 0
        ? values.meeting_points.map((point, index) => (index === editingIndex ? draft : point))
        : [...values.meeting_points, draft]
    update({ meeting_points })
    closeMeetingPointEditor()
  }

  // 打診/正式依頼テンプレ(編集時のみ): デフォルト選択は owner post の紐づけ行に対して行う(通常のツアー設定タブと同じ部品)
  const renderTemplateGroups = (resource: TemplateResource, templates?: any[]) => {
    if (props.isNew || props.ownerPostId == null) {
      return <Note>{tJa('dmc_tour.template_note_for_new')}</Note>
    }

    return (
      <>
        <DefaultTemplateGroups
          resource={resource}
          postId={props.ownerPostId}
          templates={templates || []}
          specialRanks={props.specialRanks || []}
          emptyNote={<Note>{tJa('dmc_tour.template_note_for_edit')}</Note>}
        />
        <CreateTemplateLink
          href={`/agent/${resource}/new?post_id=${props.ownerPostId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button
            small={true}
            textColor={constants.COLORS.primary}
            borderColor={constants.COLORS.primary}
          >
            {tJa('generic.create')}
          </Button>
        </CreateTemplateLink>
      </>
    )
  }

  return (
    <>
      {/* 基本情報 */}
      <Section id="dmc-basic">
        <SectionTitle>{tJa('post.input_basic_info')}</SectionTitle>
        <FormItem>
          <InputText
            required={true}
            name="short_name"
            label={tJa('post.short_name')}
            value={values.short_name}
            onChangeHandler={e =>
              update({
                short_name: e.target.value,
                title: values.title_same_as_short_name ? e.target.value : values.title,
              })
            }
          />
        </FormItem>
        <FormItem>
          <InputRadio
            name="post_type"
            label={tJa('post.tour_type')}
            options={POST_TYPE_OPTIONS}
            defaultValue={values.post_type}
            directionRow={true}
            disabled={!props.isNew}
            onChangeHandler={e =>
              update({
                post_type: e.target.value,
                exclusive: e.target.value === 'group' && values.exclusive,
              })
            }
          />
          {/* 貸切/カスタムはグループツアーのみ。ツアーの種類と違い作成後も変更できる */}
          {values.post_type === 'group' && (
            <SubOptions>
              <InputRadio
                name="exclusive"
                options={GROUP_KIND_OPTIONS}
                defaultValue={String(values.exclusive)}
                directionRow={true}
                onChangeHandler={e => update({ exclusive: e.target.value === 'true' })}
              />
            </SubOptions>
          )}
          {!props.isNew && <Note>{tJa('dmc_tour.post_type_locked')}</Note>}
        </FormItem>
        <FormItem>
          <Select
            required={true}
            name="area_id"
            label={tJa('generic.area')}
            options={[{ value: '', label: tJa('generic.select_area') }].concat(
              props.areas.map(area => ({ value: String(area.id), label: area.name }))
            )}
            value={values.area_id}
            onChangeHandler={e => update({ area_id: e.target.value })}
          />
        </FormItem>
        <FormItem>
          <InputText
            type="number"
            name="tour_group_max_attendees"
            label={tJa('post.tour_group_max_attendees')}
            value={values.tour_group_max_attendees}
            onChangeHandler={e => update({ tour_group_max_attendees: e.target.value })}
          />
        </FormItem>
        <FormItem>
          <WithLabel>
            <InputLabel label={tJa('post.language')} />
            <div className="input">
              <ReactSelect
                name="language_ids"
                placeholder={tJa('generic.select_language')}
                options={languageOptions}
                value={selectedLanguages}
                isMulti={true}
                filterOption={filterLanguageOption}
                onChange={(selected: any) =>
                  update({ language_ids: (selected || []).map(option => option.value) })
                }
                styles={LANGUAGE_SELECT_STYLES}
                menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              />
            </div>
          </WithLabel>
        </FormItem>
      </Section>

      {/* 詳細情報 */}
      <Section id="dmc-detail">
        <SectionTitle>{tJa('post.input_detail_info')}</SectionTitle>
        <FormItem>
          <InputText
            required={true}
            name="title"
            label={tJa('post.title')}
            value={values.title}
            readonly={values.title_same_as_short_name}
            onChangeHandler={e => update({ title: e.target.value })}
          />
          <WithLabel className="option">
            <InputLabel label="" />
            <CheckBox
              name="title_same_as_short_name"
              label={tJa('dmc_tour.title_same_as_short_name')}
              showLabelInline={true}
              singleCheckBox={true}
              checked={values.title_same_as_short_name}
              onChangeHandler={e =>
                update({
                  title_same_as_short_name: e.target.checked,
                  title: e.target.checked ? values.short_name : values.title,
                })
              }
            />
          </WithLabel>
        </FormItem>
      </Section>

      {/* ガイド料金 */}
      <Section id="dmc-guide-fee">
        <SectionTitle>{tJa('post.input_guide_fee')}</SectionTitle>
        <FormInnerGrid>
          <div className="innerItem">
            {props.agentRanks.length > 0 ? (
              props.agentRanks.map(rank => (
                <FormItem key={rank.id}>
                  <WithLabel>
                    <RankLabel>
                      {rank.rank_kind === 'special' && (
                        <span className="badge">{tJa('agent_rank.kind_special')}</span>
                      )}
                      {rank.name}
                      {rank.rank_kind === 'normal' && <Required />}
                    </RankLabel>
                    <div className="input">
                      <InputText
                        type="number"
                        required={rank.rank_kind === 'normal'}
                        name={`post_rank_fee_${rank.id}`}
                        value={
                          values.post_rank_fees.find(fee => fee.agent_rank_id === rank.id)
                            ?.amount ?? ''
                        }
                        onChangeHandler={e =>
                          update({
                            post_rank_fees: values.post_rank_fees
                              .filter(fee => fee.agent_rank_id !== rank.id)
                              .concat({ agent_rank_id: rank.id, amount: e.target.value }),
                          })
                        }
                      />
                    </div>
                  </WithLabel>
                </FormItem>
              ))
            ) : (
              <a href="/agent/agent_ranks">{tJa('post.set_rank')}</a>
            )}
          </div>
        </FormInnerGrid>
      </Section>

      {/* 集合場所 */}
      <Section id="dmc-address">
        <SectionTitle>{tJa('post.input_meeting_point')}</SectionTitle>
        {editingIndex === null ? (
          <MeetingPointList>
            {values.meeting_points.map((point, index) => (
              <li key={index}>
                <div>
                  <div className="name">{point.name}</div>
                  <div className="location">{point.location}</div>
                </div>
                <div>
                  <Button
                    small={true}
                    textColor={constants.COLORS.white}
                    backgroundColor={constants.COLORS.primary}
                    handleClick={() => startEditMeetingPoint(index)}
                  >
                    {tJa('generic.edit')}
                  </Button>
                  <Button
                    small={true}
                    textColor={constants.COLORS.primary}
                    borderColor={constants.COLORS.primary}
                    handleClick={() =>
                      update({
                        meeting_points: values.meeting_points.filter((_, i) => i !== index),
                      })
                    }
                  >
                    {tJa('generic.delete')}
                  </Button>
                </div>
              </li>
            ))}
            <li className="buttons">
              <Button
                small={true}
                textColor={constants.COLORS.white}
                backgroundColor={constants.COLORS.primary}
                handleClick={() => startEditMeetingPoint(-1)}
              >
                {tJa('generic.create_new')}
              </Button>
            </li>
          </MeetingPointList>
        ) : (
          <>
            <AutoComplete>
              <input
                id="dmc-meeting-point-location"
                type="text"
                placeholder={tJa('dmc_tour.meeting_point_location')}
                defaultValue={draft.location}
                onChange={e => setDraft({ ...draft, location: e.target.value })}
              />
            </AutoComplete>
            <div
              id="dmc-meeting-point-map"
              className="map"
              style={{ display: draft.latitude && draft.longitude ? 'block' : 'none' }}
            />
            <FormItem>
              <InputText
                required={true}
                name="meeting_point_name"
                label="Name"
                value={draft.name}
                onChangeHandler={e => setDraft({ ...draft, name: e.target.value })}
              />
            </FormItem>
            <FormItem>
              <InputTextArea
                name="meeting_point_memo"
                label="Memo"
                value={draft.memo}
                onChangeHandler={e => setDraft({ ...draft, memo: e.target.value })}
              />
            </FormItem>
            <Actions>
              <Button small={true} handleClick={closeMeetingPointEditor}>
                {tJa('generic.cancel')}
              </Button>
              <Button
                small={true}
                textColor={constants.COLORS.white}
                backgroundColor={constants.COLORS.primary}
                handleClick={saveMeetingPoint}
              >
                {editingIndex >= 0 ? tJa('generic.update') : tJa('generic.add')}
              </Button>
            </Actions>
          </>
        )}
      </Section>

      {/* コスト */}
      <Section id="dmc-cost">
        <SectionTitle>{tJa('activerecord.models.post_cost')}</SectionTitle>
        <FormItem>
          <InputText
            type="number"
            required={true}
            name="default_travel_cost"
            label={tJa('activerecord.attributes.post_cost.default_travel_cost')}
            value={values.post_cost.default_travel_cost}
            onChangeHandler={e =>
              update({
                post_cost: {
                  ...values.post_cost,
                  default_travel_cost: Number(e.target.value) || 0,
                },
              })
            }
          />
        </FormItem>
        <FormInner>
          <SubTitle>{tJa('activerecord.attributes.post_cost.special_activity_fee')}</SubTitle>
          <div className="innerItem">
            {[
              ...SPECIAL_ACTIVITY_FEE_KEYS,
              'special_activity_fee_over_base',
              'special_activity_fee_over_unit',
            ].map(key => (
              <FormItem key={key}>
                <InputText
                  type="number"
                  required={true}
                  name={key}
                  label={tJa(`activerecord.attributes.post_cost.${key}`)}
                  value={values.post_cost[key]}
                  onChangeHandler={e =>
                    update({
                      post_cost: { ...values.post_cost, [key]: Number(e.target.value) || 0 },
                    })
                  }
                />
              </FormItem>
            ))}
          </div>
        </FormInner>
        <FormInner>
          <SubTitle>{tJa('activerecord.attributes.post_cost.large_group')}</SubTitle>
          <div className="innerItem">
            {['large_group_min_people', 'large_group_fee'].map(key => (
              <FormItem key={key}>
                <InputText
                  type="number"
                  required={true}
                  name={key}
                  label={tJa(`activerecord.attributes.post_cost.${key}`)}
                  value={values.post_cost[key]}
                  onChangeHandler={e =>
                    update({
                      post_cost: { ...values.post_cost, [key]: Number(e.target.value) || 0 },
                    })
                  }
                />
              </FormItem>
            ))}
          </div>
        </FormInner>
      </Section>

      {/* 設定 */}
      <Section id="dmc-settings">
        <SectionTitle>{tJa('generic.setting')}</SectionTitle>
        <FormItem>
          <WithLabel>
            <InputLabel label={tJa('generic.sound_out_template')} />
            <div className="input">
              {renderTemplateGroups('sound_out_templates', props.soundOutTemplates)}
            </div>
          </WithLabel>
        </FormItem>
        <FormItem>
          <WithLabel>
            <InputLabel label={tJa('generic.formal_request_templates')} />
            <div className="input">
              {renderTemplateGroups('formal_request_templates', props.formalRequestTemplates)}
            </div>
          </WithLabel>
        </FormItem>
        <FormItem>
          <WithLabel>
            <InputLabel label={tJa('generic.tour_skills')} />
            <div className="input tourSkill">
              <TourSkillSingleSelect
                className="tourSkillSelect"
                options={props.tourSkills.map(skill => ({
                  value: String(skill.id),
                  label: skill.name,
                }))}
                value={values.post_tour_skill.tour_skill_id || null}
                onChange={value =>
                  update({
                    post_tour_skill: { ...values.post_tour_skill, tour_skill_id: value || '' },
                  })
                }
                placeholder={tJa('generic.select_skill')}
                isClearable={true}
              />
              <Select
                name="level"
                options={[{ value: '', label: tJa('generic.select_level') }].concat(
                  Object.keys(constants.SKILL_LEVELS).map(key => ({
                    value: key,
                    label: constants.SKILL_LEVELS[key],
                  }))
                )}
                value={values.post_tour_skill.level}
                onChangeHandler={e =>
                  update({ post_tour_skill: { ...values.post_tour_skill, level: e.target.value } })
                }
              />
            </div>
          </WithLabel>
        </FormItem>
        <FormItem>
          <Select
            name="guide_meeting_time_offset"
            label={tJa('generic.guide_meeting_time_offset')}
            options={buildGuideMeetingTimeOffsetOptions()}
            value={values.guide_meeting_time_offset}
            onChangeHandler={e => update({ guide_meeting_time_offset: e.target.value })}
          />
        </FormItem>
      </Section>
    </>
  )
}

const Section = styled.section`
  margin-bottom: 24px;
  padding: 0 24px 22px;
  background-color: ${constants.COLORS.white};
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 6px;
  // アンカー移動時にサイトヘッダーの下に見出しが隠れないようにする
  scroll-margin-top: ${constants.SCROLLING_HEADER_HEIGHT + 16}px;

  .map {
    height: 320px;
    margin: 16px 0;
  }
`

const SectionTitle = styled.h3`
  margin: 0 -24px 8px;
  padding: 14px 24px;
  font-size: 14px;
  font-weight: bold;
  border-bottom: 1px solid ${constants.COLORS.divider2};
`

const FormInnerGrid = styled.div`
  .innerItem {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 24px;
    align-items: start;
  }
`

const FormInner = styled.div`
  display: flex;
  border-top: 1px solid ${constants.COLORS.lightGray};
`

const SubTitle = styled.div`
  margin: 24px 0 8px;
  font-size: 14px;
  font-weight: bold;
  width: 180px;
`

const FormItem = styled.div`
  padding: 14px 0;

  & + & {
    border-top: 1px solid ${constants.COLORS.lightGray};
    .innerItem & {
      border-top: 0;
    }
  }
`

const WithLabel = styled.div`
  display: flex;
  align-items: flex-start;

  > .input {
    flex: 1;
    min-width: 0;
  }

  &.option {
    margin-top: 8px;
  }

  // 通常ツアーの設定タブ(PostSettings)のツアースキルと同じ配置
  > .tourSkill {
    display: flex;
    gap: 10px;
    align-items: center;

    .tourSkillSelect {
      width: 100%;
      max-width: 400px;
      flex-grow: 1;
    }
  }
`

// ガイド料金のランク名(InputLabelと同じ幅。特殊ランクはバッジ付き)
const RankLabel = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px 8px;
  width: 180px;
  padding: 8px 12px 8px 0;
  font-size: 14px;
  font-weight: bold;
  word-break: break-word;
  flex-wrap: wrap;

  .badge {
    flex-shrink: 0;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: normal;
    color: ${constants.COLORS.subText};
    border: 1px solid ${constants.COLORS.divider};
    border-radius: 4px;
  }
`

// ツアーの種類の2行目。ラベル列(InputLabel の幅)の分だけ字下げして1行目の選択肢に揃える
const SubOptions = styled.div`
  margin-top: 8px;
  padding-left: 180px;

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    padding-left: 0;
  }
`

const Note = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${constants.COLORS.subText};
`

const CreateTemplateLink = styled.a`
  display: inline-block;
  margin-top: 16px;
`

const MeetingPointList = styled.ul`
  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;

    &.buttons {
      justify-content: flex-start;
    }
  }

  li + li {
    border-top: solid 1px ${constants.COLORS.divider2};
  }

  .name {
    font-weight: bold;
  }

  .location {
    margin-top: 4px;
    font-size: 13px;
    color: ${constants.COLORS.subText};
  }
`

const AutoComplete = styled.div`
  > input {
    display: block;
    width: 100%;
    padding: 12px;
    border: solid 1px ${constants.COLORS.divider};
    border-radius: 4px;
    font-size: 16px;
    outline: none;
    background: none;
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`
